import { ResponseSnackbar } from 'layout/snackbar/Snackbar'
import { ApiRequest } from 'library/APICall'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * LocalizationContext
 * Each "screen" or "section" loads its own chunk of translations (e.g. pos-dashboard, pos-order-table-screen)
 * plus a shared "common" chunk that's always available for repeated words (Save, Cancel, etc).
 */
const LocalizationContext = createContext(null)

export function LocalizationProvider({
    children,
    defaultLanguage,
    registeredTranslationKeys,
    setRegisteredTranslationKeys
}) {
    const [activeLanguage, setActiveLanguage] = useState(defaultLanguage)
    const [translationGroups, setTranslationGroups] = useState({})
    const [isLoading, setIsLoading] = useState(false)
    const [textDirection, setTextDirection] = useState('ltr') // For future use (rtl will be used for Arabic language)

    useEffect(() => {
        if (defaultLanguage && defaultLanguage !== activeLanguage) {
            setActiveLanguage(defaultLanguage)
        }
    }, [defaultLanguage])

    // Refetch whenever the registered key list or the active language changes.
    useEffect(() => {
        if (!activeLanguage) return
        if (!registeredTranslationKeys || registeredTranslationKeys.length === 0) return

        setIsLoading(true)
        ApiRequest({
            TYPE: 'POST',
            URL: '/Application/getLocalization',
            CALLBACK: (rawRes) => {
                let res = rawRes;
                if (typeof res === 'string') {
                    try {
                        res = JSON.parse(res);
                    } catch (e) {
                        console.error('Failed to parse response', e);
                    }
                }

                if(res.error_code != "0" && res.error_code != 0) {
                    ResponseSnackbar(res)
                    return
                }

                const parsedData = {};
                if (res && res.data) {
                    Object.keys(res.data).forEach(key => {
                        if (key !== 'session_status') {
                            let val = res.data[key];

                            // Recursively parse in case it's double-stringified
                            while (typeof val === 'string') {
                                try {
                                    let newVal = JSON.parse(val);
                                    // Avoid infinite loops if JSON.parse(string) returns a string
                                    if (typeof newVal === 'string' && newVal === val) break;
                                    val = newVal;
                                } catch (e) {
                                    break; // Stop parsing if it fails and use the current string/object value
                                }
                            }

                            parsedData[key] = val;
                        }
                    });
                } else {
                    console.error("API response does not contain 'data' property", res);
                }

                setTranslationGroups((prev) => ({ ...prev, ...parsedData }))

                // For future use if Arabic language is used in our system
                if (res.data?.common?.meta?.direction) {
                    setTextDirection(res.data.common.meta.direction)
                }
            },
            COMPLETE: () => {
                setIsLoading(false)
            },
            DATA: {
                language: activeLanguage,
                translation_keys: registeredTranslationKeys
            }
        })
    }, [registeredTranslationKeys, activeLanguage])

    const changeLanguage = useCallback((newLanguage) => {
        setActiveLanguage(newLanguage)
    }, [])

    // Checks if a translation group is already loaded. 
    // This is useful for screens that want to know if their translations are ready yet.
    const isGroupLoaded = useCallback(
        (groupName) => Boolean(translationGroups[groupName?.split('.')[0]]),
        [translationGroups]
    )

    // Adds a key to the registered list if it isn't already there. This is what actually
    // triggers a refetch, but only the first time a given key is added.
    const registerTranslationKey = useCallback((key) => {
        if (!key) return
        const rootKey = key.split('.')[0]
        setRegisteredTranslationKeys((prev) => (prev.includes(rootKey) ? prev : [...prev, rootKey]))
    }, [setRegisteredTranslationKeys])


    /**
     * translate("groupName.path.to.text", { variable: "value" })
     * Automatically extracts the group name from the first dot segment if it exists.
     * Otherwise, assumes the "common" group.
     */
    const translate = useCallback((fullKey, variables) => {
        const parts = fullKey.split('.')
        const firstSegment = parts[0]

        let groupName = "common"
        let textPath = fullKey

        // If the first part of the dot notation matches a loaded group (e.g., 'dashboard'), 
        // separate it from the rest of the path.
        if (translationGroups[firstSegment]) {
            groupName = firstSegment
            textPath = parts.slice(1).join('.') || fullKey
        }

        // Try to find the text in the requested group first
        const foundInRequestedGroup = findTextInObject(translationGroups[groupName], textPath)
        if (typeof foundInRequestedGroup === "string") {
            return fillPlaceholders(foundInRequestedGroup, variables)
        }

        // If it wasn't found there, try looking in the "common" fallback group using the original full key
        const foundInCommonGroup = findTextInObject(translationGroups.common, fullKey)
        if (typeof foundInCommonGroup === "string") {
            return fillPlaceholders(foundInCommonGroup, variables)
        }

        // If default value is provided
        if (variables && variables.defaultValue !== undefined) {
            // If it's a string, we can still fill in any {{ variables }} and parse [formatting]
            if (typeof variables.defaultValue === "string") {
                return fillPlaceholders(variables.defaultValue, variables)
            }
            // If it is JSX (an object) or a raw number, just return it directly!
            return variables.defaultValue
        }

        // If we still can't find it anywhere, just return the exact string.
        return fullKey
    }, [translationGroups])


    // Final context values
    const contextValue = useMemo(() => ({
        translate,
        t: translate, // 't' is used as alias for translate
        activeLanguage,
        textDirection,
        changeLanguage,
        registerTranslationKey,
        isGroupLoaded,
        isLoading,
    }), [translate, activeLanguage, textDirection, changeLanguage, registerTranslationKey, isGroupLoaded, isLoading])


    return (
        <LocalizationContext.Provider value={contextValue}>
            {children}
        </LocalizationContext.Provider>
    )
}

// Hook to use the localization context anywhere in the app
export function useLocalization() {
    const context = useContext(LocalizationContext)
    if (!context) {
        throw new Error("useLocalization must be used inside a LocalizationProvider wrapper")
    }
    return context
}

/**
 * Hook for specific screens.
 * It automatically makes sure the translations for that specific screen are loaded,
 * and it scopes the translate function so you don't have to keep typing the group name.
 */
export function useTranslation(groupName) {
    const { translate, t, registerTranslationKey, isGroupLoaded, ...rest } = useLocalization()

    // Register the translation group for this screen when the component mounts
    useEffect(() => {
        registerTranslationKey(groupName)
    }, [groupName, registerTranslationKey])

    // Create a custom translate function just for this screen
    const sectionTranslate = useCallback((key, variables) => {
        // If they explicitly reference 'common' or the current group, pass it through directly
        if (key.startsWith("common.") || key.startsWith(`${groupName}.`)) {
            return translate(key, variables)
        }

        // Otherwise, automatically attach this screen's group name to the front using a dot.
        return translate(`${groupName}.${key}`, variables)
    }, [translate, groupName])

    return {
        ...rest,
        translate: sectionTranslate,
        t: sectionTranslate, // Shortcut alias
        registerTranslationKey,
        isGroupLoaded,
        isReady: isGroupLoaded(groupName),
    }
}

// Digs through a deeply nested JSON object to find a specific text string.
// Example: findTextInObject(obj, "dashboard.event_card.today.title") returns "आज"
function findTextInObject(objectToSearch, pathString) {
    if (!objectToSearch) return undefined;

    return pathString
        .split(".")
        .reduce((currentLevel, key) => {
            return (currentLevel && currentLevel[key] !== undefined) ? currentLevel[key] : undefined
        }, objectToSearch)
}

// Replaces placeholders like {{ name }} in a string with actual values.
// Also convert [text] to an underline <u> tag to support UI shortcuts.
function fillPlaceholders(textString, variablesToInsert) {
    if (typeof textString !== 'string') return textString

    // Use a regex with a capturing group to split the text. 
    // This matches either {{ variable }} OR [shortcut]
    const regex = /(\{\{\s*\w+\s*\}\}|\[.*?\])/g
    
    // Split text into chunks. Capturing groups ensure matched delimiters stay in the array.
    const parts = textString.split(regex)

    // Normal strings if no variables or shortcuts
    if (parts.length === 1) {
        return textString
    }

    return parts.map((part, index) => {
        // Handle {{ variables }}
        const varMatch = part.match(/^\{\{\s*(\w+)\s*\}\}$/)
        if (varMatch) {
            const variableName = varMatch[1]
            if (variablesToInsert && Object.prototype.hasOwnProperty.call(variablesToInsert, variableName)) {
                return variablesToInsert[variableName]
            }
            return part // Return the raw text if variable wasn't provided
        }

        // Handle [shortcuts]
        const underlineMatch = part.match(/^\[(.*?)\]$/)
        if (underlineMatch) {
            return <u key={index}>{underlineMatch[1]}</u>
        }

        // Regular text chunks
        return part
    })
}

export default LocalizationContext